import { CategoryIdHumanized } from '@/constants'
import {
  AnalyticsContentType,
  type VideoSourceInfoOVP,
} from '@setplex/tria-api'
import type { CurrentProgram } from '~/shared/types/epg'

export type EpgPlayingAnalyticsData = {
  programTitle: string | undefined
  programStart: string
  duration?: string
}

export type PlayStartEpgCategory = {
  categoryId?: CategoryIdHumanized | string
  categoryTitle?: string
} | null

export type PlayerStreamVariant =
  | 'playStart'
  | 'playStop'
  | 'playerWatching'
  | 'playerEvent'

export function isEpgContentType(contentType?: string): boolean {
  return (
    contentType === AnalyticsContentType.live ||
    contentType === AnalyticsContentType.liveRewind ||
    contentType === AnalyticsContentType.catchup
  )
}

/** Mirrors legacy tria-api-adapter-ovp event_parameter12 mapping per event. */
export function getProgramOrEpisodeId(
  d: VideoSourceInfoOVP,
  variant: PlayerStreamVariant
): string {
  if (isEpgContentType(d.contentType)) {
    if (variant === 'playStart' || variant === 'playerWatching') {
      return String(d.programStart ?? '')
    }
    return String(d.contentStart ?? '')
  }
  return String(d.episodeId ?? d.episodeCode ?? '')
}

/** Mirrors legacy tria-api-adapter-ovp event_parameter13 mapping per event. */
export function getProgramOrEpisodeTitle(
  d: VideoSourceInfoOVP,
  variant: PlayerStreamVariant
): string {
  if (isEpgContentType(d.contentType)) {
    if (variant === 'playStart' || variant === 'playerWatching') {
      return String(d.programTitle ?? '')
    }
    return String(d.contentTitle ?? '')
  }
  return String(d.episodeCode ?? '')
}

export function mapCurrentProgramToEpgAnalytics(
  program: CurrentProgram
): EpgPlayingAnalyticsData | null {
  if (program == null) return null

  const base = {
    programTitle: program.programTitleForAnalytics,
    programStart: program.programStartForAnalytics,
  }

  if (program.isLive || program.isRewinded) {
    return {
      ...base,
      duration: program.durationForAnalytics ?? '0',
    }
  }

  return base
}

export function getPlayStartCategoryTitle(
  isEpgPage: boolean,
  category: PlayStartEpgCategory
): string {
  if (!isEpgPage) return ''

  const { categoryId, categoryTitle } = category || {}
  if (categoryId === CategoryIdHumanized.Favorite) {
    return CategoryIdHumanized.Favorite
  }
  if (categoryId === CategoryIdHumanized.All) {
    return CategoryIdHumanized.All
  }
  return categoryTitle ?? ''
}
